import { Fragment, useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../../../stores/authStore";
import { useProfileModalStore } from "../../../../stores/profileModalStore";
import { useAlertStore } from "../../../../stores/alertStore";
import { userService, type UpdateProfileRequest } from "../../../../lib/api/users";
import { subscriptionsService, isShopifySubscription, type Subscription, type BillingInvoice, type PaymentMethod, type BillingStatus } from "../../../../lib/api/subscriptions";
import { buildShopifyManageBillingUrl } from "../../../../lib/shopifyAdminUrl";
import { ShoppingBag as ShoppingBagIcon } from "lucide-react";
import { ConfirmationModal } from "../../../../components/ConfirmationModal";
import { activityService, type ActivityLog } from "../../../../lib/api/activity";
import { useThemeStore } from "../../../../stores/themeStore";
import { CookieSettingsButton } from "../../../../components/gdpr";

export const ProfileModal = () => {
    const { t } = useTranslation("common");
    const { isOpen, closeProfileModal, activeTab, setActiveTab } = useProfileModalStore();
    const { theme, setTheme } = useThemeStore();
    const { user, setUser } = useAuthStore();
    const { showAlert } = useAlertStore();
    const titleRef = useRef(null);

    // Local state for forms
    const [isUpdating, setIsUpdating] = useState(false);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Initial form data
    const [formData, setFormData] = useState({
        name: user?.name || "",
        email: user?.email || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        currency: user?.currency || "",
        timezone: user?.timezone || "UTC",
    });

    // Reset form when user changes or modal opens
    useEffect(() => {
        if (isOpen && user) {
            setFormData(prev => ({
                ...prev,
                name: user.name || "",
                email: user.email || "",
                currency: user.currency || "",
                timezone: user.timezone || "UTC"
            }));
            setHasUnsavedChanges(false);
        }
    }, [isOpen, user]);

    // Notification settings local state
    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        channelAlerts: true,
        weeklyReports: false,
        monthlyReports: true,
        securityAlerts: true,
        marketingEmails: false,
    });



    // Billing State
    const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
    const [isLoadingBilling, setIsLoadingBilling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    // Address State
    const [customerAddress, setCustomerAddress] = useState<any>(null);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [isSavingAddress, setIsSavingAddress] = useState(false);
    const [addressForm, setAddressForm] = useState({
        line1: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
    });

    // Activity Log State
    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
    const [activityTotal, setActivityTotal] = useState(0);
    const [activityOffset, setActivityOffset] = useState(0);
    const [activityCategory, setActivityCategory] = useState<string>('');
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [activityHasMore, setActivityHasMore] = useState(false);

    const fetchBilling = async () => {
        setIsLoadingBilling(true);
        try {
            // Load billingStatus first — when billingSource is "shopify" the
            // billing tab renders a Shopify-managed card and skips the
            // Stripe-shaped UI entirely, so the rest of the fetch can run
            // best-effort without blocking that render path.
            try {
                const bsRes = await subscriptionsService.getBillingStatus();
                if (bsRes.success && bsRes.data) {
                    setBillingStatus(bsRes.data);
                }
            } catch {
                // Non-fatal — fall through; Stripe UI will render as before.
            }

            const subRes = await subscriptionsService.getMySubscriptions();
            let currentSub: Subscription | null = null;
            if (subRes.success && subRes.data && subRes.data.length > 0) {
                // Ignore Shopify subscriptions for the Stripe billing UI — a lapsed
                // Shopify sub is past_due and would falsely show a "payment issue".
                currentSub = subRes.data.find((s) => !isShopifySubscription(s)) || null;
                setSubscription(currentSub);
            } else {
                setSubscription(null);
            }

            let loadedInvoices: BillingInvoice[] = [];
            const invRes = await subscriptionsService.getInvoices();
            if (invRes.success && invRes.data) {
                loadedInvoices = invRes.data;
                setInvoices(loadedInvoices);
            }

            const pmRes = await subscriptionsService.getPaymentMethods();
            if (pmRes.success && pmRes.data) {
                setPaymentMethods(pmRes.data);
            }

            const custRes = await subscriptionsService.getCustomer();
            if (custRes.success && custRes.data) {
                setCustomerAddress(custRes.data.address || {});
                setAddressForm({
                    line1: custRes.data.address?.line1 || '',
                    city: custRes.data.address?.city || '',
                    state: custRes.data.address?.state || '',
                    postalCode: custRes.data.address?.postal_code || '',
                    country: custRes.data.address?.country || ''
                });
            }

            // If subscription exists but no invoices yet, the payment webhook
            // is likely still processing. Retry a few times with a short delay.
            if (currentSub && loadedInvoices.length === 0) {
                for (let retry = 0; retry < 3; retry++) {
                    await new Promise(r => setTimeout(r, 3000));
                    const retryInv = await subscriptionsService.getInvoices();
                    if (retryInv.success && retryInv.data && retryInv.data.length > 0) {
                        setInvoices(retryInv.data);
                        break;
                    }
                }
            }

            // Fallback: If no subscription found locally, try syncing from Stripe
            // This only runs when data appears to be missing, NOT on every load
            if (!currentSub) {
                try {
                    const syncResult = await subscriptionsService.syncBilling();
                    if (syncResult.synced) {
                        // Re-fetch after sync
                        const reSub = await subscriptionsService.getMySubscriptions();
                        if (reSub.success && reSub.data && reSub.data.length > 0) {
                            setSubscription(reSub.data.find((s) => !isShopifySubscription(s)) || null);
                        }
                        const reInv = await subscriptionsService.getInvoices();
                        if (reInv.success && reInv.data) {
                            setInvoices(reInv.data);
                        }
                    }
                } catch {
                    // Non-fatal
                }
            }
        } catch (error) {
            console.error("Failed to fetch billing info", error);
        } finally {
            setIsLoadingBilling(false);
        }
    };

    // Fetch billing info
    useEffect(() => {
        if (isOpen && activeTab === 'billing') {
            fetchBilling();
        }
    }, [isOpen, activeTab]);

    // Fetch activity logs
    const fetchActivity = async (reset = false) => {
        setIsLoadingActivity(true);
        try {
            const offset = reset ? 0 : activityOffset;
            const res = await activityService.getActivity({
                limit: 20,
                offset,
                category: activityCategory || undefined,
            });
            if (res.success && res.data) {
                if (reset) {
                    setActivityLogs(res.data.logs);
                } else {
                    setActivityLogs(prev => [...prev, ...res.data.logs]);
                }
                setActivityTotal(res.data.pagination.total);
                setActivityHasMore(res.data.pagination.hasMore);
                setActivityOffset(offset + res.data.logs.length);
            }
        } catch (error) {
            console.error('Failed to fetch activity logs', error);
        } finally {
            setIsLoadingActivity(false);
        }
    };

    useEffect(() => {
        if (isOpen && activeTab === 'activity') {
            setActivityOffset(0);
            fetchActivity(true);
        }
    }, [isOpen, activeTab, activityCategory]);

    const handleAddPaymentMethod = async () => {
        try {
            const res = await subscriptionsService.createSetupSession();
            if (res.url) {
                window.location.href = res.url;
            }
        } catch (error) {
            showAlert({ message: t("userErrors.subscription.unableToStartSetup"), type: "error" });
        }
    };

    const handleDeletePaymentMethod = async (id: string) => {
        if (!confirm(t("profile.billing.confirmRemovePaymentMethod"))) return;
        try {
            await subscriptionsService.deletePaymentMethod(id);
            fetchBilling(); // Refresh list
        } catch (error) {
            showAlert({ message: t("userErrors.subscription.failedToRemovePaymentMethod"), type: "error" });
        }
    };

    const handleSaveAddress = async () => {
        setIsSavingAddress(true);
        try {
            const res = await subscriptionsService.updateCustomer(addressForm);
            if (res.success && res.data) {
                setCustomerAddress(res.data.address);
                setIsEditingAddress(false);

            }
        } catch (error) {
            console.error("Failed to update address", error);
            showAlert({ message: t("userErrors.subscription.failedToUpdateAddress"), type: "error" });
        } finally {
            setIsSavingAddress(false);
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleCancelSubscription = () => {
        setShowCancelConfirm(true);
    };

    const confirmCancelSubscription = async () => {
        setShowCancelConfirm(false);
        try {
            await subscriptionsService.cancelSubscription();
            fetchBilling();
        } catch (error) {
            showAlert({ message: t("userErrors.subscription.failedToCancelSubscription"), type: "error" });
        }
    };

    const handleResumeSubscription = async () => {
        try {
            await subscriptionsService.resumeSubscription();
            fetchBilling();
        } catch (error) {
            showAlert({ message: t("userErrors.subscription.failedToResumeSubscription"), type: "error" });
        }
    };

    const handleCancelPlanChangeSchedule = async () => {
        try {
            await subscriptionsService.cancelPlanChangeSchedule();
            fetchBilling();
            showAlert({ message: t("profile.billing.scheduleCanceled", "Plan change schedule canceled successfully"), type: "success" });
        } catch (error) {
            showAlert({ message: t("userErrors.subscription.failedToCancelSchedule", "Failed to cancel plan change schedule"), type: "error" });
        }
    };

    const handleDeleteAccount = async () => {
        if (!user?.id) return;
        
        setIsDeletingAccount(true);
        try {
            const res = await userService.deleteAccount(user.id);
            if (res.success) {
                showAlert({ message: t("profile.privacy.accountDeleted"), type: "success" });
                // Log out the user or redirect
                setTimeout(() => {
                    window.location.href = "/logout"; // Adjust based on your auth flow
                }, 2000);
            }
        } catch (error) {
            showAlert({ message: t("userErrors.profile.failedToDeleteAccount"), type: "error" });
        } finally {
            setIsDeletingAccount(false);
            setShowDeleteAccountConfirm(false);
        }
    };

    // Handlers
    const handleInputChange = (field: string, value: string) => {
        setFormData({ ...formData, [field]: value });
        setHasUnsavedChanges(true);
    };

    const handleUpdateProfile = async () => {
        if (!formData.name.trim()) {
            showAlert({ message: t("userErrors.profile.nameRequired"), type: "error" });
            return;
        }

        setIsUpdating(true);
        try {
            const updateData: UpdateProfileRequest = {
                name: formData.name,
                email: formData.email,
                currency: formData.currency,
                timezone: formData.timezone,
            };

            const response = await userService.updateProfile(updateData);

            if (response.success && response.data.user) {
                setUser({
                    ...user!,
                    name: response.data.user.name,
                    email: response.data.user.email,
                    currency: response.data.user.currency,
                    timezone: response.data.user.timezone,
                });
                setHasUnsavedChanges(false);
            }
        } catch (error: any) {
            // Use friendly error message - don't expose backend errors
            let friendlyMessage = t("userErrors.profile.unableToUpdate");
            const serverError = error.response?.data?.error?.toLowerCase() || "";

            if (serverError.includes("email") && (serverError.includes("taken") || serverError.includes("exists"))) {
                friendlyMessage = t("userErrors.profile.emailInUse");
            } else if (error.response?.status === 400) {
                friendlyMessage = t("userErrors.profile.checkInfo");
            }

            showAlert({
                message: friendlyMessage,
                type: "error",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleNotificationToggle = (field: string) => {
        setNotifications({
            ...notifications,
            [field]: !notifications[field as keyof typeof notifications],
        });
    };



    // Render functions
    const renderGeneralSettings = () => (
        <div className="space-y-6">


            {/* Theme Selector */}
            <div className="bg-neutral-50 dark:bg-neutral-800 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700">
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    {t("profile.appearance", "Appearance")}
                </label>
                <div className="grid grid-cols-3 gap-2">
                    <button
                        onClick={() => setTheme('light')}
                        className={`flex items-center justify-center p-2 rounded border ${theme === 'light'
                            ? 'bg-white dark:bg-neutral-700 border-accent-primary text-accent-primary font-medium shadow-sm'
                            : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600'
                            }`}
                    >
                        <span className="w-4 h-4 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1a7c9999.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1a7c9999.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        {t("profile.themeLight", "Light")}
                    </button>
                    <button
                        onClick={() => setTheme('dark')}
                        className={`flex items-center justify-center p-2 rounded border ${theme === 'dark'
                            ? 'bg-white dark:bg-neutral-700 border-accent-primary text-accent-primary font-medium shadow-sm'
                            : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600'
                            }`}
                    >
                        <span className="w-4 h-4 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f11fe1bd.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f11fe1bd.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        {t("profile.themeDark", "Dark")}
                    </button>
                    <button
                        onClick={() => setTheme('system')}
                        className={`flex items-center justify-center p-2 rounded border ${theme === 'system'
                            ? 'bg-white dark:bg-neutral-700 border-accent-primary text-accent-primary font-medium shadow-sm'
                            : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300 dark:hover:border-neutral-600'
                            }`}
                    >
                        <span className="w-4 h-4 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_aa66fa73.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_aa66fa73.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        {t("profile.themeSystem", "Auto")}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        {t("auth.name")}
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        className="input"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        {t("auth.email")}
                    </label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className="input"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        {t("profile.defaultCurrency")}
                    </label>
                    <select
                        value={formData.currency}
                        onChange={(e) => handleInputChange("currency", e.target.value)}
                        className="input"
                    >
                        <option value="USD">{t('currency.usd')}</option>
                        <option value="EUR">{t('currency.eur')}</option>
                        <option value="GBP">{t('currency.gbp')}</option>
                        <option value="JPY">{t('currency.jpy')}</option>
                        <option value="CAD">{t('currency.cad')}</option>
                        <option value="AUD">{t('currency.aud')}</option>
                        <option value="CHF">{t('currency.chf')}</option>
                        <option value="CNY">{t('currency.cny')}</option>
                        <option value="INR">{t('currency.inr')}</option>
                        <option value="BRL">{t('currency.brl')}</option>
                        <option value="TND">{t('currency.tnd')}</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        {t("profile.timezone")}
                    </label>
                    <select
                        value={formData.timezone}
                        onChange={(e) => handleInputChange("timezone", e.target.value)}
                        className="input"
                    >
                        <option value="UTC">{t('timezones.utc')}</option>
                        <option value="America/New_York">{t('timezones.usEastern')}</option>
                        <option value="America/Chicago">{t('timezones.usCentral')}</option>
                        <option value="America/Denver">{t('timezones.usMountain')}</option>
                        <option value="America/Los_Angeles">{t('timezones.usPacific')}</option>
                        <option value="Europe/London">{t('timezones.europeLondon')}</option>
                        <option value="Europe/Paris">{t('timezones.europeParis')}</option>
                        <option value="Europe/Berlin">{t('timezones.europeBerlin')}</option>
                        <option value="Asia/Dubai">{t('timezones.asiaDubai')}</option>
                        <option value="Asia/Kolkata">{t('timezones.asiaKolkata')}</option>
                        <option value="Asia/Singapore">{t('timezones.asiaSingapore')}</option>
                        <option value="Asia/Tokyo">{t('timezones.asiaTokyo')}</option>
                        <option value="Australia/Sydney">{t('timezones.australiaSydney')}</option>
                    </select>
                </div>
            </div>

            {/* Password Change Toggle */}
            <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
                {!showPasswordForm ? (
                    <button
                        onClick={() => setShowPasswordForm(true)}
                        className="text-sm font-medium text-accent-primary hover:text-accent-primary/80"
                    >
                        {t("profile.changePassword")}
                    </button>
                ) : (
                    <div className="space-y-4 bg-neutral-50 dark:bg-neutral-800 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700">
                        <div className="flex justify-between items-center">
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{t("profile.changePassword")}</h4>
                            <button onClick={() => setShowPasswordForm(false)} className="text-xs text-neutral-500 dark:text-neutral-400">{t('actions.cancel')}</button>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                {t("profile.currentPassword")}
                            </label>
                            <input
                                type="password"
                                value={formData.currentPassword}
                                onChange={(e) => handleInputChange("currentPassword", e.target.value)}
                                className="input text-sm py-2"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    {t("profile.newPassword")}
                                </label>
                                <input
                                    type="password"
                                    value={formData.newPassword}
                                    onChange={(e) => handleInputChange("newPassword", e.target.value)}
                                    className="input text-sm py-2"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    {t("auth.confirmPassword")}
                                </label>
                                <input
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                                    className="input text-sm py-2"
                                />
                            </div>
                        </div>
                        <button className="btn-secondary text-xs w-full">{t('profile.updatePassword')}</button>
                    </div>
                )}
            </div>
        </div>
    );

    const renderNotificationsSettings = () => (
        <div className="space-y-4">
            {[
                {
                    key: "emailNotifications",
                    label: t("profile.emailNotificationsLabel"),
                    desc: t("profile.emailNotificationsDesc"),
                },
                {
                    key: "channelAlerts",
                    label: t("profile.channelAlerts"),
                    desc: t("profile.channelAlertsDesc"),
                },
                {
                    key: "weeklyReports",
                    label: t("profile.weeklyReports"),
                    desc: t("profile.weeklyReportsDesc"),
                },
                {
                    key: "monthlyReports",
                    label: t("profile.monthlyReports"),
                    desc: t("profile.monthlyReportsDesc"),
                },
            ].map((item) => (
                <div
                    key={item.key}
                    className="flex items-center justify-between py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-0"
                >
                    <div className="flex-1 pr-4">
                        <h4 className="text-sm font-medium text-text-primary dark:text-neutral-100">
                            {item.label}
                        </h4>
                        <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">{item.desc}</p>
                    </div>
                    <button
                        onClick={() => handleNotificationToggle(item.key)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${notifications[item.key as keyof typeof notifications]
                            ? "bg-accent-primary"
                            : "bg-neutral-300 dark:bg-neutral-600"
                            }`}
                    >
                        <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${notifications[item.key as keyof typeof notifications]
                                ? "translate-x-5"
                                : "translate-x-1"
                                }`}
                        />
                    </button>
                </div>
            ))}
        </div>
    );

    const renderBillingSettings = () => {
        // Shopify-billed users: never show Stripe UI. Plan management, cancel,
        // and payment-method changes all happen in Shopify Admin. This mirrors
        // PlanPage's compliance gate so the Profile modal can't accidentally
        // route them through Stripe-backed endpoints (which are also guarded
        // server-side, but failing client-side is a better UX).
        // Read-only "Billed via Shopify" only while a Shopify subscription is
        // ACTIVE. Once lapsed (grace/read_only/suspended), the account is "open"
        // → show the normal billing UI so they can subscribe via the website.
        if (
            billingStatus?.billingSource === "shopify" &&
            billingStatus?.billingStatus === "active"
        ) {
            const shopAdminUrl = buildShopifyManageBillingUrl(billingStatus.shopifyBillingShop);
            return (
                <div className="space-y-6">
                    <div className="bg-white dark:bg-neutral-900 p-6 rounded-lg border border-neutral-200 dark:border-neutral-800 text-center space-y-4">
                        <div className="mx-auto w-12 h-12 rounded-full bg-shopify/10 flex items-center justify-center">
                            <ShoppingBagIcon className="w-6 h-6 text-shopify" />
                        </div>
                        <h4 className="text-base font-medium text-neutral-900 dark:text-neutral-100">
                            {t("billing.managedByShopify.title", "Billed via Shopify")}
                        </h4>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
                            {t("billing.managedByShopify.body", "Your subscription is managed in your Shopify admin. Update payment methods, change plans, or cancel from there.")}
                        </p>
                        {shopAdminUrl && (
                            <a
                                href={shopAdminUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary inline-block px-6 py-2.5"
                            >
                                {t("billing.managedByShopify.openShopify", "Manage in Shopify")}
                            </a>
                        )}
                    </div>
                </div>
            );
        }

        return (
        <>
            <ConfirmationModal
                isOpen={showCancelConfirm}
                title={t('profile.cancelSubscription')}
                message={t('profile.billing.cancelSubscriptionMessage')}
                confirmText={t('profile.billing.confirmCancelAction')}
                cancelText={t('profile.billing.keepPlanAction')}
                onConfirm={confirmCancelSubscription}
                onCancel={() => setShowCancelConfirm(false)}
                variant="danger"
            />
            <div className="space-y-6">
                {(subscription?.status === 'past_due' || subscription?.status === 'incomplete') && (
                    <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
                        <div className="flex justify-between items-center">
                            <div>
                                <h4 className="text-sm font-medium text-error-800 dark:text-error-400">
                                    {t('profile.billing.paymentIssue', 'Payment Issue')}
                               </h4>
                                <p className="text-sm text-error-700 dark:text-error-300 mt-1">
                                    {t('profile.billing.paymentIssueDesc', 'Your last payment failed. Please update your payment method.')}
                                </p>
                            </div>
                            <button
                                onClick={handleAddPaymentMethod}
                                className="px-3 py-1.5 bg-error-600 hover:bg-error-700 text-white text-xs font-medium rounded shadow-sm transition-colors"
                            >
                                {t('profile.billing.updatePaymentMethod', 'Update payment method')}
                            </button>
                        </div>
                    </div>
                )}
                {/* Current Plan Section */}
                <div className="bg-white dark:bg-neutral-900 p-4 rounded-lg border border-neutral-200 dark:border-neutral-800">
                    <div className="flex justify-between items-start">
                        <div>
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{t('profile.currentPlan')}</h4>
                            <p className="text-lg font-bold text-text-primary dark:text-white mt-1">
                                {subscription?.status === 'trialing' ? t('profile.freeTrial') : (subscription?.variant?.product?.name || t('profile.noSubscriptions'))}
                            </p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                {subscription ? (
                                    subscription.status === 'trialing' ? (
                                        (() => {
                                            const now = new Date();
                                            const end = new Date(subscription.currentPeriodEnd);
                                            const diffMs = end.getTime() - now.getTime();
                                            const diffDays = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
                                            if (diffDays === 0) return t('profile.trialExpiresToday', 'Trial expires today');
                                            return t('profile.trialDaysLeft', { count: diffDays, defaultValue: '{{count}} days left in trial' });
                                        })()
                                    ) : (
                                        `${formatCurrency(subscription.price.amount / 100, subscription.price.currency)} / ${subscription.price.interval}`
                                    )
                                ) : (
                                    <a href="/plan" target="_blank" rel="noreferrer" className="text-accent-primary hover:underline">
                                        {t('profile.viewPlans')}
                                    </a>
                                )}
                            </p>
                        </div>
                        {subscription && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${subscription.cancelAtPeriodEnd
                                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                                : subscription.status === 'active' || subscription.status === 'trialing'
                                    ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400'
                                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                                }`}>
                                {subscription.cancelAtPeriodEnd ? t('profile.canceling') : subscription.status.toUpperCase()}
                            </span>
                        )}
                    </div>

                    {/* Cancellation Warning */}
                    {subscription?.cancelAtPeriodEnd && (
                        <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <p className="text-sm text-amber-800 dark:text-amber-400">
                                <span className="font-medium">{t('profile.cancellationScheduled')}</span> {t('profile.billing.cancellationNotice', { date: formatDate(subscription.currentPeriodEnd) })}
                            </p>
                        </div>
                    )}

                    {/* Pending Plan Change Warning */}
                    {(() => {
                        if (!subscription?.metadata) return null;
                        try {
                            const meta = typeof subscription.metadata === "string" ? JSON.parse(subscription.metadata) : subscription.metadata;
                            if (meta.pendingPlanName && meta.pendingChangeDate) {
                                return (
                                    <div className="mt-3 p-3 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg">
                                        <p className="text-sm text-accent-800 dark:text-accent-400">
                                            <span className="font-medium">
                                                {t('profile.billing.switchingTo', { defaultValue: 'Switching to {{plan}} on', plan: meta.pendingPlanName })}
                                            </span>{' '}
                                            {formatDate(meta.pendingChangeDate)}
                                        </p>
                                    </div>
                                );
                            }
                        } catch (e) {}
                        return null;
                    })()}

                    <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex justify-between items-center">
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">
                            {subscription?.currentPeriodEnd
                                ? subscription.cancelAtPeriodEnd
                                    ? t('profile.billing.endsOn', { date: formatDate(subscription.currentPeriodEnd) })
                                    : t('profile.billing.renewsOn', { date: formatDate(subscription.currentPeriodEnd) })
                                : ""}
                        </p>
                        {subscription && !subscription.cancelAtPeriodEnd && (
                            <button
                                onClick={handleCancelSubscription}
                                className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-error-600 dark:hover:text-error-400 font-medium"
                            >
                                {subscription.status === 'trialing' ? t('profile.cancelTrial', 'Cancel trial') : t('profile.cancelPlan')}
                            </button>
                        )}
                        {subscription?.cancelAtPeriodEnd && (
                            <button
                                onClick={handleResumeSubscription}
                                className="text-sm text-accent-primary hover:text-accent-primary/80 font-medium"
                            >{t('profile.keepSubscription')}</button>
                        )}
                        {(() => {
                            if (!subscription?.metadata) return null;
                            try {
                                const meta = typeof subscription.metadata === "string" ? JSON.parse(subscription.metadata) : subscription.metadata;
                                if (meta.pendingPriceId) {
                                    return (
                                        <button
                                            onClick={handleCancelPlanChangeSchedule}
                                            className="text-sm text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300 font-medium"
                                        >
                                            {t('profile.billing.cancelSchedule', 'Cancel schedule')}
                                        </button>
                                    );
                                }
                            } catch (e) {}
                            return null;
                        })()}
                    </div>
                </div>

                {/* Billing Address Hidden for now */}
                {false && (
                    <div className="bg-white dark:bg-neutral-900 p-4 rounded-lg border border-neutral-200 dark:border-neutral-800">
                        <div className="flex justify-between items-center mb-3">
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{t('profile.billingAddress')}</h4>
                            {!isEditingAddress ? (
                                <button onClick={() => setIsEditingAddress(true)} className="text-xs text-accent-primary hover:underline">{t('actions.edit')}</button>
                            ) : (
                                <div className="flex gap-2">
                                    <button onClick={() => setIsEditingAddress(false)} className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300">{t('actions.cancel')}</button>
                                    <button onClick={handleSaveAddress} disabled={isSavingAddress} className="text-xs text-accent-primary font-medium hover:underline">
                                        {isSavingAddress ? t('profile.saving') : t('actions.save')}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingAddress ? (
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    placeholder={t('profile.addressLine1') as string}
                                    className="input text-sm col-span-2"
                                    value={addressForm.line1}
                                    onChange={e => setAddressForm({ ...addressForm, line1: e.target.value })}
                                />
                                <input
                                    placeholder={t('profile.city') as string}
                                    className="input text-sm"
                                    value={addressForm.city}
                                    onChange={e => setAddressForm({ ...addressForm, city: e.target.value })}
                                />
                                <input
                                    placeholder={t('profile.state') as string}
                                    className="input text-sm"
                                    value={addressForm.state}
                                    onChange={e => setAddressForm({ ...addressForm, state: e.target.value })}
                                />
                                <input
                                    placeholder={t('profile.zipCode') as string}
                                    className="input text-sm"
                                    value={addressForm.postalCode}
                                    onChange={e => setAddressForm({ ...addressForm, postalCode: e.target.value })}
                                />
                                <input
                                    placeholder={t('profile.countryPlaceholder') as string}
                                    className="input text-sm"
                                    value={addressForm.country}
                                    onChange={e => setAddressForm({ ...addressForm, country: e.target.value })}
                                />
                            </div>
                        ) : (
                            <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                {customerAddress && (customerAddress.line1 || customerAddress.city) ? (
                                    <>
                                        <p>{customerAddress.line1}</p>
                                        {customerAddress.line2 && <p>{customerAddress.line2}</p>}
                                        <p>{[customerAddress.city, customerAddress.state, customerAddress.postal_code].filter(Boolean).join(', ')}</p>
                                        <p>{customerAddress.country}</p>
                                    </>
                                ) : (
                                    <p className="italic text-neutral-400">{t('profile.noAddressSet')}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div className="space-y-3">
                    <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{t('profile.paymentMethods')}</h4>
                    {paymentMethods.length > 0 ? (
                        <div className="space-y-2">
                            {paymentMethods.map(method => (
                                <div key={method.id} className="flex items-center justify-between bg-white dark:bg-neutral-900 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-6 bg-neutral-100 dark:bg-neutral-800 rounded flex items-center justify-center text-neutral-500 text-[10px] font-bold tracking-wider uppercase">
                                            {method.brand}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium leading-none text-neutral-900 dark:text-neutral-100">•••• {method.last4}</p>
                                            <p className="text-xs text-neutral-500 mt-1">Expires {method.expMonth}/{method.expYear}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleDeletePaymentMethod(method.id)}
                                        className="text-xs font-medium text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300"
                                    >{t('actions.remove')}</button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-neutral-500 italic">{t('profile.noPaymentMethods')}</p>
                    )}

                    <button
                        onClick={handleAddPaymentMethod}
                        className="text-sm text-accent-primary hover:underline font-medium flex items-center gap-1"
                    >
                        <span className="text-lg">+</span> {t('profile.addPaymentMethod')}
                    </button>
                </div>
                <div>
                    <h4 className="text-sm font-medium mb-3 text-neutral-900 dark:text-neutral-100">{t('profile.billingHistory')}</h4>

                    {isLoadingBilling ? (
                        <div className="text-center py-8 text-neutral-500 text-sm">{t('profile.loadingBillingHistory')}</div>
                    ) : invoices.length === 0 ? (
                        <div className="text-center py-8 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-100 dark:border-neutral-800">
                            <p className="text-neutral-500 text-sm">{t('profile.noBillingHistory')}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {invoices.map((item) => (
                                <div key={item.id} className="flex justify-between items-start text-sm py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                    <div className="flex-1 pr-4">
                                        <p className="font-medium text-text-primary dark:text-neutral-100 text-xs">{formatDate(item.date)}</p>
                                        {item.description && <p className="text-sm text-neutral-800 dark:text-neutral-300 my-1">{item.description}</p>}
                                        <div className="flex items-center gap-2">
                                            <p className="text-xs text-neutral-400">Invoice #{item.id.slice(-6).toUpperCase()}</p>
                                            {(item.hostedInvoiceUrl || item.invoicePdf) && (
                                                <a href={item.hostedInvoiceUrl || item.invoicePdf} target="_blank" rel="noopener noreferrer" className="text-xs text-accent-primary hover:underline">
                                                    {t('profile.downloadInvoice')}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="font-medium text-neutral-900 dark:text-neutral-100">{formatCurrency(item.amount, item.currency)}</span>
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${item.status === 'PAID' ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                                            }`}>{item.status}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
        );
    };

    const renderPrivacySettings = () => (
        <div className="space-y-8">
            <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg">
                                <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3a929b6c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3a929b6c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </span>
                            <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                                {t("gdpr.preferences.title")}
                            </h3>
                        </div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                            {t("gdpr.preferences.description")}
                        </p>
                        <CookieSettingsButton
                            variant="button"
                            className="!bg-neutral-900 text-white hover:!bg-neutral-800 dark:!bg-white dark:text-neutral-900 dark:hover:!bg-neutral-100"
                            onClick={closeProfileModal}
                        />
                    </div>
                </div>
            </div>

            {/* Account Deletion / Facebook Data Deletion */}
            {(() => {
                // Shopify-billed merchants with an active or grace-period
                // subscription can't delete their Dashbal account directly:
                // their subscription is anchored on the Shopify side and
                // deleting here would orphan the still-billing AppSubscription.
                // Force them through "uninstall on Shopify" first — the
                // app/uninstalled webhook then starts the grace timer that
                // eventually suspends the account, after which delete is
                // allowed again.
                const isShopifyLocked =
                    billingStatus?.billingSource === "shopify" &&
                    (billingStatus?.billingStatus === "active" ||
                        billingStatus?.billingStatus === "grace_period");
                const shopAdminUrl = buildShopifyManageBillingUrl(billingStatus?.shopifyBillingShop);

                if (isShopifyLocked) {
                    return (
                        <div className="bg-shopify/5 border border-shopify/20 rounded-xl p-6">
                            <div className="flex items-start gap-4">
                                <div className="p-2 bg-shopify/10 text-shopify rounded-lg">
                                    <ShoppingBagIcon className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                                        {t("profile.privacy.shopifyDeleteLockedTitle", "Uninstall from Shopify first")}
                                    </h3>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                                        {t(
                                            "profile.privacy.shopifyDeleteLockedDesc",
                                            "Your subscription is managed by Shopify. To delete your account, first uninstall the Dashbal app from your Shopify admin — this cancels the subscription and starts the data-removal grace period. Once that's done, you'll be able to delete your account here.",
                                        )}
                                    </p>
                                    {shopAdminUrl && (
                                        <a
                                            href={shopAdminUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 bg-shopify hover:bg-shopify/90 text-white rounded-lg text-sm font-medium transition-colors"
                                        >
                                            {t("billing.managedByShopify.openShopify", "Manage in Shopify")}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="bg-error-50/30 dark:bg-error-900/10 border border-error-100 dark:border-error-900/30 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="p-2 bg-error-50 dark:bg-error-900/20 text-error-600 dark:text-error-400 rounded-lg">
                                <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f2aa5288.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f2aa5288.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-base font-semibold text-error-900 dark:text-error-400 mb-1">
                                    {t("profile.privacy.deleteAccountTitle", "Danger Zone")}
                                </h3>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
                                    {t("profile.privacy.deleteAccountDesc", "Permanently delete your account and all associated data, including Facebook platform data. This action cannot be undone.")}
                                </p>
                                <button
                                    onClick={() => setShowDeleteAccountConfirm(true)}
                                    className="inline-flex items-center px-4 py-2 border border-error-300 dark:border-error-800 rounded-lg text-sm font-medium text-error-700 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/30 transition-colors"
                                >
                                    {t("profile.privacy.deleteAccountBtn", "Delete Account & Data")}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <ConfirmationModal
                isOpen={showDeleteAccountConfirm}
                title={t('profile.privacy.confirmDeleteAccountTitle', 'Delete Account?')}
                message={t('profile.privacy.confirmDeleteAccountMsg', 'Are you sure you want to delete your account? All your data, including connected Facebook data, will be permanently removed from our servers.')}
                confirmText={isDeletingAccount ? t('actions.deleting', 'Deleting...') : t('actions.delete', 'Delete Permanently')}
                cancelText={t('actions.cancel')}
                onConfirm={handleDeleteAccount}
                onCancel={() => setShowDeleteAccountConfirm(false)}
                variant="danger"
            />
        </div>
    );

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'security':
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f2aa5288.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f2aa5288.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case 'payment':
            case 'subscription':
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_29f56e84.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_29f56e84.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case 'channel':
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_157f99b8.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_157f99b8.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case 'board':
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_13cd22b5.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_13cd22b5.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            case 'user':
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_a574c6c9.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a574c6c9.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
            default:
                return (
                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3f551057.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3f551057.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                );
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'security':
                return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800';
            case 'payment':
            case 'subscription':
                return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800';
            case 'channel':
                return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
            case 'board':
                return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
            case 'user':
                return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800';
            default:
                return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700';
        }
    };

    const getStatusIndicator = (status: string) => {
        switch (status) {
            case 'success':
                return 'bg-success-500';
            case 'failure':
                return 'bg-error-500';
            case 'warning':
                return 'bg-amber-500';
            default:
                return 'bg-neutral-400';
        }
    };

    const formatActivityDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t("time.justNow");
        if (diffMins < 60) return t("time.minsAgo", { count: diffMins });
        if (diffHours < 24) return t("time.hoursAgo", { count: diffHours });
        if (diffDays < 7) return t("time.daysAgo", { count: diffDays });
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };

    const renderActivitySettings = () => (
        <div className="space-y-4">
            {/* Filter Row */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-neutral-500">{t("profile.filter")}:</span>
                    <select
                        value={activityCategory}
                        onChange={(e) => setActivityCategory(e.target.value)}
                        className="text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary"
                    >
                        <option value="">{t("profile.allActivity")}</option>
                        <option value="security">{t("profile.categories.security")}</option>
                        <option value="payment">{t("profile.categories.payment")}</option>
                        <option value="channel">{t("profile.categories.channel")}</option>
                        <option value="board">{t("profile.categories.board")}</option>
                        <option value="user">{t("profile.categories.user")}</option>
                    </select>
                </div>
                <span className="text-xs text-neutral-400">
                    {activityTotal} {activityTotal === 1 ? t("profile.event") : t("profile.events")}
                </span>
            </div>

            {/* Activity Timeline */}
            {isLoadingActivity && activityLogs.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent-primary border-t-transparent"></div>
                </div>
            ) : activityLogs.length === 0 ? (
                <div className="text-center py-12 bg-neutral-50 rounded-xl border border-neutral-100">
                    <span className="w-12 h-12 mx-auto text-neutral-300 mb-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_fba4f3d5.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_fba4f3d5.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    <p className="text-neutral-500">{t("profile.noActivity")}</p>
                    <p className="text-xs text-neutral-400 mt-1">{t("profile.activityDescription")}</p>
                </div>
            ) : (
                <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-[19px] top-0 bottom-0 w-px bg-neutral-200" />

                    <div className="space-y-0">
                        {activityLogs.map((log) => (
                            <div key={log.id} className="relative flex gap-3 py-3 group">
                                {/* Timeline dot */}
                                <div className="relative z-10 flex-shrink-0">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${getCategoryColor(log.category)}`}>
                                        {getCategoryIcon(log.category)}
                                    </div>
                                    {/* Status indicator */}
                                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getStatusIndicator(log.status)}`} />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0 pt-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-text-primary truncate">
                                                {log.action}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${getCategoryColor(log.category)}`}>
                                                    {log.category}
                                                </span>
                                                {log.ipAddress && (
                                                    <span className="text-xs text-neutral-400">from {log.ipAddress}</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-xs text-neutral-400 whitespace-nowrap flex-shrink-0">
                                            {formatActivityDate(log.createdAt)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Load More */}
                    {activityHasMore && (
                        <div className="pt-4 text-center">
                            <button
                                onClick={() => fetchActivity(false)}
                                disabled={isLoadingActivity}
                                className="text-sm text-accent-primary hover:text-accent-primary/80 font-medium disabled:opacity-50"
                            >
                                {isLoadingActivity ? t("profile.loadingActivity") : t("profile.loadMoreActivity")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const tabs = [
        { id: "general", label: t("profile.general"), icon: "user" },
        { id: "activity", label: t("profile.activity"), icon: "clock" },
        { id: "notifications", label: t("profile.notifications"), icon: "bell" },
        { id: "billing", label: t("profile.billing.label"), icon: "credit-card" },
        { id: "privacy", label: t("profile.privacy.label"), icon: "shield" },
        { id: "categories", label: t("profile.categories.label"), icon: "tag" },
    ];

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={closeProfileModal} initialFocus={titleRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop transition-opacity bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-3xl transform rounded-xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-800 text-left align-middle shadow-2xl transition-all overflow-hidden flex flex-col md:flex-row h-[85vh] md:h-[600px]">
                                {/* Sidebar */}
                                <div className="w-full md:w-56 border-b md:border-b-0 md:border-r border-neutral-100 dark:border-neutral-800 flex-shrink-0 flex flex-col select-none bg-neutral-50/50 dark:bg-neutral-900/50 overflow-hidden">
                                    <div className="p-5 border-b border-neutral-100 dark:border-neutral-800 md:block hidden">
                                        <h2
                                            className="text-base font-bold text-neutral-900 dark:text-neutral-100 focus:outline-none"
                                            ref={titleRef}
                                            tabIndex={-1}
                                        >
                                            {t("navigation.settings", "Settings")}
                                        </h2>
                                    </div>
                                    <div className="p-2 space-y-0.5 md:overflow-y-auto overflow-x-auto flex flex-row md:flex-col flex-1 scrollbar-hide">
                                        {tabs.map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`flex-shrink-0 md:w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab.id
                                                    ? "bg-white dark:bg-neutral-800 text-accent-primary shadow-sm ring-1 ring-black/5 dark:ring-white/5"
                                                    : "text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-200"
                                                    }`}
                                            >
                                                <div className={`hidden md:block w-1 h-4 rounded-full transition-colors ${activeTab === tab.id ? "bg-accent-primary" : "bg-transparent"}`} />
                                                <span className="whitespace-nowrap">{tab.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Content Area */}
                                <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-neutral-900">
                                    <div className="px-4 py-3 md:px-6 md:py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between shrink-0">
                                        <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                                            {tabs.find(t => t.id === activeTab)?.label}
                                        </h3>
                                        <button
                                            onClick={closeProfileModal}
                                            className="text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 p-1.5 rounded-full hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                                        >
                                            <span className="sr-only">Close</span>
                                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d872171d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d872171d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar">
                                        <div className="max-w-2xl mx-auto w-full">
                                            {activeTab === "general" && renderGeneralSettings()}
                                            {activeTab === "activity" && renderActivitySettings()}
                                            {activeTab === "notifications" && renderNotificationsSettings()}
                                            {activeTab === "billing" && renderBillingSettings()}
                                            {activeTab === "privacy" && renderPrivacySettings()}
                                        </div>
                                    </div>

                                    {/* Footer / Actions */}
                                    {hasUnsavedChanges && activeTab === "general" && (
                                        <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
                                            <span className="text-sm text-amber-600 dark:text-amber-500 font-medium px-2">{t('profile.unsavedChanges')}</span>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, name: user?.name || "", email: user?.email || "" }));
                                                        setHasUnsavedChanges(false);
                                                    }}
                                                    className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
                                                >{t('actions.discard')}</button>
                                                <button
                                                    onClick={handleUpdateProfile}
                                                    disabled={isUpdating}
                                                    className="btn-primary py-2 px-6 text-sm"
                                                >
                                                    {isUpdating ? t("profile.saving") : t("profile.saveChanges")}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
